import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { AuditLog, AuditLogDetalles, FilterOptions, LogsQueryParams, Pagination } from '../types/AuditLog';
import auditService from '../services/auditService';

interface LogsTableProps {
  onError?: (error: string) => void;
}

const LogsTable: React.FC<LogsTableProps> = ({ onError }) => {
  const [data, setData] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    usuarios: [],
    tiposDocumento: [],
    resultados: ['encontrado', 'no_encontrado', 'error']
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Filtros del servidor
  const [serverFilters, setServerFilters] = useState<LogsQueryParams>({
    page: 1,
    limit: 50
  });

  // Cargar opciones de filtros al montar
  useEffect(() => {
    const loadFilterOptions = async () => {
      const response = await auditService.getFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    };
    loadFilterOptions();
  }, []);

  // Cargar datos cuando cambian los filtros
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const response = await auditService.getLogs(serverFilters);

      if (response.success) {
        setData(response.data);
        setPagination(response.pagination);
      } else {
        onError?.(response.error || 'Error al cargar logs');
      }
      setLoading(false);
    };
    loadData();
  }, [serverFilters, onError]);

  // Parsear detalles JSON
  const parseDetalles = (detalles: string | null): AuditLogDetalles | null => {
    if (!detalles) return null;
    try {
      return JSON.parse(detalles);
    } catch {
      return null;
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Aplicar filtro del servidor
  const applyFilter = (key: keyof LogsQueryParams, value: string) => {
    setServerFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: 1 // Reset a página 1 cuando se filtra
    }));
  };

  // Definir columnas
  const columns = useMemo<ColumnDef<AuditLog>[]>(() => [
    {
      accessorKey: 'timestamp',
      header: 'Timestamp (UTC)',
      cell: ({ row }) => (
        <span className="text-sm font-mono">
          {row.original.timestamp}
        </span>
      ),
    },
    {
      accessorKey: 'usuario',
      header: () => (
        <div className="space-y-1">
          <span>Usuario</span>
          <select
            className="w-full text-xs p-1 border rounded bg-white"
            onChange={(e) => applyFilter('usuario', e.target.value)}
            defaultValue=""
          >
            <option value="">Todos</option>
            {filterOptions.usuarios.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      ),
    },
    {
      accessorKey: 'tipo_documento',
      header: () => (
        <div className="space-y-1">
          <span>Tipo Doc</span>
          <select
            className="w-full text-xs p-1 border rounded bg-white"
            onChange={(e) => applyFilter('tipoDocumento', e.target.value)}
            defaultValue=""
          >
            <option value="">Todos</option>
            {filterOptions.tiposDocumento.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      ),
    },
    {
      accessorKey: 'numero_documento',
      header: () => (
        <div className="space-y-1">
          <span>Nro Documento</span>
          <input
            type="text"
            className="w-full text-xs p-1 border rounded"
            placeholder="Buscar..."
            onChange={(e) => {
              // Debounce para no hacer muchas peticiones
              const value = e.target.value;
              const timeoutId = setTimeout(() => {
                applyFilter('numeroDocumento', value);
              }, 500);
              return () => clearTimeout(timeoutId);
            }}
          />
        </div>
      ),
    },
    {
      accessorKey: 'resultado',
      header: () => (
        <div className="space-y-1">
          <span>Resultado</span>
          <select
            className="w-full text-xs p-1 border rounded bg-white"
            onChange={(e) => applyFilter('resultado', e.target.value)}
            defaultValue=""
          >
            <option value="">Todos</option>
            {filterOptions.resultados.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      ),
      cell: ({ row }) => {
        const resultado = row.original.resultado;
        const badgeClass = resultado === 'encontrado'
          ? 'bg-green-100 text-green-800'
          : resultado === 'no_encontrado'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-red-100 text-red-800';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
            {resultado}
          </span>
        );
      },
    },
    {
      accessorKey: 'detalles',
      header: 'Detalles',
      cell: ({ row }) => {
        const detalles = parseDetalles(row.original.detalles);
        const isExpanded = expandedRows.has(row.original.id);

        if (!detalles) {
          return <span className="text-gray-400 text-sm">-</span>;
        }

        return (
          <div>
            <button
              onClick={() => toggleRowExpansion(row.original.id)}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              {isExpanded ? 'Ocultar' : 'Ver detalles'}
            </button>
            {isExpanded && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                {detalles.nombre && (
                  <p><strong>Nombre:</strong> {detalles.nombre} {detalles.apellido}</p>
                )}
                {detalles.planesActivos && detalles.planesActivos.length > 0 && (
                  <p><strong>Planes:</strong> {detalles.planesActivos.join(', ')}</p>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ], [filterOptions, expandedRows]);

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount: pagination.totalPages,
  });

  // Cambiar página
  const goToPage = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setServerFilters(prev => ({ ...prev, page }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Leyenda UTC */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
        <strong>Nota:</strong> Los timestamps se muestran en UTC (Tiempo Universal Coordinado).
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  Cargando...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  No se encontraron registros
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-4 py-3 text-sm text-gray-900">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-700">
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
          {pagination.total} registros
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => goToPage(1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {'<<'}
          </button>
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {'<'}
          </button>
          <span className="text-sm">
            Página {pagination.page} de {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {'>'}
          </button>
          <button
            onClick={() => goToPage(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {'>>'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogsTable;
